import { LanguagesEnum } from '@/api/enums';
import {
  ContentLanguageTabs,
  useContentLanguageLabel,
} from '@/ui/content-language-tabs';
import React from 'react';
import { useTranslation } from 'react-i18next';
// TODO: move to '@/ui/rich-text' — react-quill is being confined there
// so the editor can be swapped in one place.
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';

const CardLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
`;

const LanguageTitle = styled.div`
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
`;

const EditorPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
  background-color: var(--beige-main);
  border-radius: var(--xsmall-border-radius);
  padding: var(--medium-big-padding);

  .quill {
    display: flex;
    flex-direction: column;
    gap: var(--medium-padding);
    background-color: transparent;
    border: none;
    min-height: auto;

    &:hover {
      border: none;
    }
  }

  .ql-toolbar.ql-snow {
    background-color: var(--white-main);
    border: 1px solid var(--gray-dark);
    border-bottom: 1px solid var(--gray-dark);
    border-radius: var(--xsmall-border-radius);
    padding: var(--medium-padding) var(--semi-big-font-size);
    text-align: left;
  }

  .ql-container.ql-snow {
    background-color: transparent;
    border: none;
    padding: 0;
    font-size: var(--medium-font-size);
  }

  .ql-editor {
    padding: 0;
    min-height: 180px;
    color: var(--black-main);
  }

  .quill .ql-container .ql-blank::before {
    left: 0;
    right: 0;
    color: var(--gray-darker);
    font-size: var(--medium-font-size);
    font-style: normal;
  }
`;

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};

export type PhishingSignCardProps = {
  languages: LanguagesEnum[];
  activeLanguage: LanguagesEnum;
  content: string;
  isEmpty: (language: LanguagesEnum) => boolean;
  onLanguageChange: (language: LanguagesEnum) => void;
  onChange: (value: string) => void;
};

export const PhishingSignCard: React.FC<PhishingSignCardProps> = ({
  languages,
  activeLanguage,
  content,
  isEmpty,
  onLanguageChange,
  onChange,
}) => {
  const { t } = useTranslation();
  const languageLabel = useContentLanguageLabel();

  return (
    <CardLayout>
      <ContentLanguageTabs
        languages={languages}
        active={activeLanguage}
        onChange={onLanguageChange}
        isEmpty={isEmpty}
      />
      <EditorPanel>
        <LanguageTitle>{languageLabel(activeLanguage)}</LanguageTitle>
        <ReactQuill
          key={activeLanguage}
          theme="snow"
          value={content}
          onChange={onChange}
          modules={quillModules}
          placeholder={t('phishingSigns.enterContent')}
        />
      </EditorPanel>
    </CardLayout>
  );
};
